import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Mail, Phone, MessageCircle, Copy, CheckCircle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

interface ContactInfo {
  email?: string;
  phone?: string;
  whatsapp?: string;
  preferred_contact_method?: string;
  is_wali?: boolean;
  contact_name?: string;
}

interface ContactInfoCardProps {
  contactInfo: ContactInfo;
  name: string;
}

export function ContactInfoCard({ contactInfo, name }: ContactInfoCardProps) {
  const [copied, setCopied] = useState<string | null>(null);
  const isWali = contactInfo.is_wali === true;

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    toast.success(`${label} copied to clipboard`);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-primary" />
          {isWali
            ? `Contact Information - ${name}'s Wali${
                contactInfo.contact_name ? ` (${contactInfo.contact_name})` : ""
              }`
            : `Contact Information - ${name}`}
        </CardTitle>
        <CardDescription>
          {isWali
            ? `Both parties have accepted. ${name} has chosen to be contacted through a wali, so the details below belong to the wali — please reach out there rather than to ${name} directly.`
            : "Both parties have accepted. You can now connect directly."}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-3">
        {contactInfo.preferred_contact_method && (
          <div className="p-3 bg-background rounded-lg border">
            <p className="text-xs text-muted-foreground mb-1">Preferred Method</p>
            <p className="font-medium">{contactInfo.preferred_contact_method}</p>
          </div>
        )}

        {contactInfo.email && (
          <div className="flex items-center justify-between p-3 bg-background rounded-lg border">
            <div className="flex items-center gap-3">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Email</p>
                <p className="font-medium">{contactInfo.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => copyToClipboard(contactInfo.email!, "Email")}
            >
              {copied === "Email" ? (
                <CheckCircle className="h-4 w-4 text-primary" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>
        )}

        {contactInfo.phone && (
          <div className="flex items-center justify-between p-3 bg-background rounded-lg border">
            <div className="flex items-center gap-3">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Phone</p>
                <p className="font-medium">{formatPhoneForDisplay(contactInfo.phone)}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => copyToClipboard(contactInfo.phone!, "Phone")}
            >
              {copied === "Phone" ? (
                <CheckCircle className="h-4 w-4 text-primary" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>
        )}

        {contactInfo.whatsapp && (
          <div className="flex items-center justify-between p-3 bg-background rounded-lg border">
            <div className="flex items-center gap-3">
              <MessageCircle className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">WhatsApp</p>
                <p className="font-medium">{formatPhoneForDisplay(contactInfo.whatsapp)}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => copyToClipboard(contactInfo.whatsapp!, "WhatsApp")}
            >
              {copied === "WhatsApp" ? (
                <CheckCircle className="h-4 w-4 text-primary" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
